import { Icon } from 'assets/images';
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { verifyAccountDeletion } from 'Services/accountDeletionService';
import {
  ACCOUNT_DELETION_API_RESPONSE_MESSAGES,
  ACCOUNT_DELETION_ERROR_MESSAGES,
  ACCOUNT_DELETION_NAVIGATION,
  ACCOUNT_DELETION_PAGE_COPY,
  ACCOUNT_DELETION_QUERY_PARAMS,
} from './constants';
import '../Password/forgotpassword.css';
import './accountDeletion.css';

/**
 * Resolves the user-facing title for a successful verification response.
 *
 * @param {string} message - Success message returned by the API.
 * @returns {string} Display title for the success state.
 */
const getVerificationSuccessTitle = (message) => {
  if (message === ACCOUNT_DELETION_API_RESPONSE_MESSAGES.ALREADY_VERIFIED) {
    return ACCOUNT_DELETION_PAGE_COPY.VERIFICATION_ALREADY_VERIFIED_TITLE;
  }
  return ACCOUNT_DELETION_PAGE_COPY.VERIFICATION_SUCCESS_TITLE;
};

/**
 * Public page that verifies an account deletion request from the email link token.
 *
 * @returns {JSX.Element} Account deletion verification page.
 */
const AccountDeletionVerification = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const token = searchParams.get(ACCOUNT_DELETION_QUERY_PARAMS.TOKEN);

  /**
   * Calls the verification API with the token from the URL.
   */
  const verifyDeletionRequest = useCallback(async () => {
    if (!token) {
      setErrorMessage(ACCOUNT_DELETION_ERROR_MESSAGES.MISSING_TOKEN);
      setLoading(false);
      return;
    }

    setLoading(true);
    setErrorMessage('');
    setSuccess(false);

    try {
      const response = await verifyAccountDeletion({ token });
      const { message } = response?.data || {};
      setSuccessMessage(
        message || ACCOUNT_DELETION_API_RESPONSE_MESSAGES.VERIFICATION_SUCCESS
      );
      setSuccess(true);
    } catch (verificationError) {
      const apiMessage = verificationError?.response?.data?.message;
      setErrorMessage(
        apiMessage || ACCOUNT_DELETION_ERROR_MESSAGES.VERIFICATION_FAILED
      );
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    verifyDeletionRequest();
  }, [verifyDeletionRequest]);

  return (
    <div className="container forgot-password-wrapper account-deletion-wrapper">
      <div className="container information-wrapper">
        <div className="icon-wrapper text-start">
          <img className="image-logo-pb" src={Icon} alt="purebrand" />
        </div>
        <div className="reset-password-title text-start">
          <h3>
            {success
              ? getVerificationSuccessTitle(successMessage)
              : ACCOUNT_DELETION_PAGE_COPY.VERIFICATION_PAGE_TITLE}
          </h3>
        </div>

        {loading ? (
          <div className="account-deletion-verification-loading text-center">
            <Spinner animation="grow" />
            <p className="mt-3">{ACCOUNT_DELETION_PAGE_COPY.VERIFICATION_LOADING}</p>
          </div>
        ) : null}

        {!loading && success ? (
          <div className="check-email-text">
            <p>{successMessage}</p>
            <div className="button-wrapper">
              <button
                type="button"
                onClick={() => navigate(ACCOUNT_DELETION_NAVIGATION.LOGIN)}
                className="btn btn-primary"
              >
                {ACCOUNT_DELETION_PAGE_COPY.VERIFICATION_GO_HOME}
              </button>
            </div>
          </div>
        ) : null}

        {!loading && !success ? (
          <div className="col-lg-8 mb-3 mt-3">
            <Alert variant="danger">{errorMessage}</Alert>
            <div className="button-wrapper">
              <button
                type="button"
                onClick={() => navigate(ACCOUNT_DELETION_NAVIGATION.LOGIN)}
                className="btn btn-primary"
              >
                {ACCOUNT_DELETION_PAGE_COPY.VERIFICATION_GO_HOME}
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default AccountDeletionVerification;
